// import-menus.ts — dependent menus for the import/export templates (compat rules §17, pure — no Excel/DOM).
//
// For each material FAMILY, the list of allowed items per axis (print / cut / lamination / finishing).
// The exported workbook wires a dependent Excel validation from these lists: the chosen material
// restricts the menus of the other columns. One source of truth for the browser export and the blank
// template script.

import { CATALOGUE, PRINTS, CUTS, LAMINATIONS, FINISHINGS } from "../../data";
import { materialFamilyOf, compatibleOptions } from "../catalogue/compatibility";
import { CATALOGUE_VERSION } from "../core/version";
import type { MenuItem, ImportMenus } from "../../models/import-menus.model";

export type { MenuItem, ImportMenus };

/**
 * Short label of a component item code (printing/cutting/lamination/finishing).
 * @returns The map from item code to short label.
 */
function tableLabels(): Map<string, string> {
  const m = new Map<string, string>();
  for (const p of PRINTS) m.set(p.code, p.label);
  for (const c of CUTS) m.set(c.code, c.label);
  for (const p of LAMINATIONS) m.set(p.code, p.label);
  for (const c of FINISHINGS) if (c.label && /^CONF-/.test(c.code)) m.set(c.code, c.label); // excludes section headers
  return m;
}

/**
 * Builds the dependent menu data (one pass over the catalogue). Pure.
 * @returns The menus: eligible materials, material-to-family table and allowed items per family and axis.
 */
export function menusImport(): ImportMenus {
  const labels = tableLabels();
  /**
   * Menu item of a code: "CODE — short label".
   * @param code The item code.
   * @returns The menu item.
   */
  const item = (code: string): MenuItem => ({ ref: code, label: `${code} — ${labels.get(code) ?? code}` });

  // Menus exclude retired components; pricing lookups keep them via the full PRINTS/CUTS tables.
  const printCodes = PRINTS.filter((p) => p.active !== false).map((p) => p.code);
  const cutCodes = CUTS.filter((c) => c.active !== false).map((c) => c.code);
  const laminationCodes = LAMINATIONS.filter((p) => p.active !== false).map((p) => p.code);
  const finishingCodes = FINISHINGS.filter(
    (c) => c.active !== false && c.label && /^CONF-/.test(c.code),
  ).map((c) => c.code);

  const materials: MenuItem[] = [];
  const materialFamily: { ref: string; family: string }[] = [];
  const families: ImportMenus["families"] = {};
  const repByFamily = new Map<string, string>(); // first representative material of a family

  for (const materialEntry of CATALOGUE) {
    if (materialEntry.active === false) continue; // menus exclude retired materials; pricing keeps them
    const fam = materialFamilyOf(materialEntry.ref);
    if (!fam) continue; // no compat family -> out of dependent menus (free entry, checked at import)
    materials.push({ ref: materialEntry.ref, label: `${materialEntry.ref} — ${materialEntry.name}` });
    materialFamily.push({ ref: materialEntry.ref, family: fam });
    if (!repByFamily.has(fam)) repByFamily.set(fam, materialEntry.ref);
  }

  // Once per family: allowed items per axis (all materials of a family share the same rule).
  for (const [fam, rep] of repByFamily) {
    families[fam] = {
      print: compatibleOptions("print", rep, printCodes).map(item),
      cut: compatibleOptions("cut", rep, cutCodes).map(item),
      lamination: compatibleOptions("lamination", rep, laminationCodes).map(item),
      finishing: compatibleOptions("finishing1", rep, finishingCodes).map(item),
    };
  }

  return { version: CATALOGUE_VERSION, materials, materialFamily, families };
}
